// src/components/auth/AuthModal.tsx
'use client';

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import GoogleAuthButton from '@/components/auth/GoogleAuthButton';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { X } from 'lucide-react';
import Image from 'next/image';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { MessageCircle, Smartphone, Shield, CheckCircle2 } from 'lucide-react';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'login' | 'register';
  redirectUrl?: string;
  onLoginSuccess?: () => void;
  actionButtonText?: string;
  /** When false, hide the close button and disable backdrop-click dismissal
   *  (used for hard login gates, e.g. the logged-out dashboard). Default true. */
  dismissible?: boolean;
  /** Override the backdrop styling. Default blurs the page behind the modal;
   *  pass e.g. 'bg-black/10' to dim WITHOUT blurring (keeps page chrome crisp). */
  backdropClassName?: string;
}

export default function AuthModal({ isOpen, onClose, mode = 'login', redirectUrl, onLoginSuccess, actionButtonText, dismissible = true, backdropClassName = 'backdrop-blur-[5px] bg-white/4' }: AuthModalProps) {
  // Login methods: one-click Google + WhatsApp OTP. Email/password is gated OFF
  // (code kept for easy re-enable — flip EMAIL_PASSWORD_ENABLED to true).
  // NOTE: WhatsApp OTP requires an approved template on the WABA behind
  // WHATSAPP_PHONE_NUMBER_ID (see WHATSAPP_OTP_TEMPLATE), else send-otp 404s.
  // Re-enabled 2 Aug 2026: the WABA restriction cleared and the account is back
  // online, so WhatsApp OTP login is live again.
  const WHATSAPP_ENABLED = true;
  const EMAIL_PASSWORD_ENABLED = false;
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentMode, setCurrentMode] = useState(mode);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [authMethod, setAuthMethod] = useState<'email' | 'whatsapp'>('whatsapp'); // Enter routes to the WhatsApp form (the only form now)
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [otpSentSuccess, setOtpSentSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [registrationStep, setRegistrationStep] = useState<'details' | 'otp'>('details');
  const router = useRouter();
  const { login, user } = useAuth();
  const otpInputRef = useRef<HTMLInputElement>(null);

  // Decide where to send the user after login
  const redirectAfterLogin = async () => {
    // If a specific redirect was requested by the opener, honor it
    if (redirectUrl) {
      window.location.href = decodeURIComponent(redirectUrl);
      return;
    }
    // Stay on the current page after login
  };

  // Reset form when modal opens/closes or mode changes
  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time synchronous form reset on modal open / mode change
      setEmail('');
      setPassword('');
      setName('');
      setError('');
      setSuccess('');
      setEmailError('');
      setCurrentMode(mode);
      setOtpSent(false);
      setResendTimer(0);
      setCanResend(false);
      setOtpSentSuccess(false);
      setRegistrationStep('details');
    }
  }, [isOpen, mode]);

  // Redirect if already logged in
  useEffect(() => {
    if (user && isOpen) {
      onClose();
      if (redirectUrl) {
        window.location.href = decodeURIComponent(redirectUrl);
      }
    }
  }, [user, isOpen, router, redirectUrl, onClose]);

  // Handle resend timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (otpSent && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [otpSent, resendTimer]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (registrationStep === 'otp' && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [registrationStep]);

  const handleRegistrationSubmit = async () => {
    try {
      const response = await fetch('/api/auth/email/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Registration failed.');
      }
      setSuccess(data.message || 'OTP has been sent to your email.');
      setRegistrationStep('otp');
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginSubmit = async () => {
    try {
      const response = await fetch('/api/auth/email/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (data.suggestion === 'google_login') throw new Error(data.error || 'Please sign in with Google instead.');
        if (data.suggestion === 'register_or_google') throw new Error(data.error || 'No account found. Please create one.');
        if (data.suggestion === 'wrong_password') throw new Error(data.error || 'Incorrect password.');
        throw new Error(data.error || 'Login failed.');
      }
      
      await login(data.token, data.user);
      
      if (onLoginSuccess) onLoginSuccess();
      onClose();
      setTimeout(async () => {
        await redirectAfterLogin();
      }, 100);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    if (currentMode === 'login') {
      handleLoginSubmit();
    } else { // currentMode === 'register'
      if (registrationStep === 'details') {
        handleRegistrationSubmit();
      }
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/email/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'OTP verification failed.');
      }

      await login(data.token, data.user);
      if (onLoginSuccess) onLoginSuccess();
      onClose();
      await redirectAfterLogin();
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'An error occurred during OTP verification.');
    } finally {
      setIsLoading(false);
    }
  };


  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific error cases
        if (data.suggestion === 'google_login') {
          throw new Error(data.error || 'Your account was created with Google. Please sign in with Google instead.');
        }
        throw new Error(data.error || 'Failed to process password reset request');
      }

      // Show success message
      setError('');
      setSuccess(data.message || 'Password reset link has been sent to your email address.');
      
      // Reset form after successful request
      setTimeout(() => {
        setIsForgotPassword(false);
        setSuccess('');
      }, 4000);
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'An error occurred while processing your request');
    } finally {
      setIsLoading(false);
    }
  };

  const handleWhatsAppAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Validate email if provided
    if (email && !validateEmail(email)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    try {
      if (!otpSent) {
        // Send OTP
        const requestBody = currentMode === 'register' 
          ? { phoneNumber, name, email }
          : { phoneNumber };

        const response = await fetch('/api/auth/whatsapp/send-otp', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to send OTP');
        }

        setOtpSent(true);
        setResendTimer(30);
        setCanResend(false);
        setOtpSentSuccess(true);
        setError('');
      } else {
        // Verify OTP
        const requestBody = currentMode === 'register'
          ? { phoneNumber, otp, name, email }
          : { phoneNumber, otp };

        const response = await fetch('/api/auth/whatsapp/verify-otp', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to verify OTP');
        }

        await login(data.token, data.user);
        
        if (onLoginSuccess) {
          onLoginSuccess();
        }
        
        onClose();
        
        if (redirectUrl) {
          // eslint-disable-next-line react-hooks/immutability -- browser navigation, not a React value
          window.location.href = decodeURIComponent(redirectUrl);
        } else {
          window.location.reload();
        }
      }
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'An error occurred during WhatsApp authentication');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;
    
    setError('');
    setIsLoading(true);

    // Validate email if provided
    if (email && !validateEmail(email)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    try {
      const requestBody = currentMode === 'register' 
        ? { phoneNumber, name, email }
        : { phoneNumber };

      const response = await fetch('/api/auth/whatsapp/send-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend OTP');
      }

      setResendTimer(30);
      setCanResend(false);
      setOtpSentSuccess(true);
      setError('');
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'An error occurred while resending OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (authMethod === 'whatsapp') {
        handleWhatsAppAuth(e as unknown as React.FormEvent);
      } else if (isForgotPassword) {
        handleForgotPassword(e as unknown as React.FormEvent);
      } else {
        handleSubmit(e as unknown as React.FormEvent);
      }
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    setEmail(emailValue);
    
    // Clear email error when user starts typing
    if (emailError) {
      setEmailError('');
    }
    
    // Validate email if it's not empty
    if (emailValue && !validateEmail(emailValue)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  if (!isOpen) return null;

  // Get Google Client ID from environment variable
  const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || process.env.GOOGLE_CLIENT_ID;
  
  if (!googleClientId) {
    console.error('Google Client ID is not configured. Please set NEXT_PUBLIC_GOOGLE_CLIENT_ID or GOOGLE_CLIENT_ID in your .env file');
    return null;
  }

  // Render into <body> via a portal so the fixed overlay is anchored to the
  // viewport, not to any ancestor with a CSS transform (e.g. the auto-hiding
  // sticky GlobalHeader), which would otherwise position it inside the header.
  if (typeof document === 'undefined') return null;

  return createPortal(
    <GoogleOAuthProvider clientId={googleClientId}>
      <div className="fixed inset-0 z-[9999] overflow-y-auto">
        {/* Backdrop */}
        <div
          className={`fixed inset-0 transition-opacity z-[9998] ${backdropClassName}`}
          onClick={dismissible ? onClose : undefined}
        />

        {/* Modal */}
        <div className="flex min-h-full items-center justify-center p-2 sm:p-4 text-center">
          <div className="relative z-[10000] transform overflow-hidden rounded-2xl bg-white px-4 sm:px-6 pb-4 sm:pb-6 pt-4 sm:pt-6 text-left shadow-2xl transition-all sm:my-8 w-full sm:max-w-lg sm:p-8 border border-zinc-200/50 backdrop-blur-sm">
            {/* Close button */}
            {dismissible && (
              <button
                onClick={onClose}
                className="absolute right-2 sm:right-4 top-2 sm:top-4 text-gray-400 hover:text-gray-500 transition-colors p-1.5 sm:p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            )}

            {/* Logo */}
            <div className="flex justify-center mb-6 sm:mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-zinc-500/10 rounded-2xl blur-xl transform -rotate-6"></div>
                <Image
                  src="/Ghackk-Logo-Horizontal.webp"
                  alt="Ghackk Technologies Logo"
                  width={350}
                  height={100}
                  className="rounded-xl shadow-lg relative z-10 transform hover:scale-105 transition-transform duration-300 w-[180px] h-[52px] sm:w-[220px] sm:h-[60px] object-contain aspect-[3.5/1]"
                  priority
                />
              </div>
            </div>

            {/* Signin/signup merged — Google & WhatsApp both create the account
                automatically for new users, so there's no separate mode. */}
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-extrabold bg-gradient-to-r from-zinc-600 to-zinc-800 bg-clip-text text-transparent mb-2 sm:mb-3">
                Sign in to continue
              </h2>
              <p className="text-sm sm:text-base text-zinc-700">
                {WHATSAPP_ENABLED
                  ? 'Continue with Google or WhatsApp — no password needed.'
                  : 'Continue with Google — no password needed.'}
              </p>
            </div>

            {/* Auth method toggle removed — Google + WhatsApp are both shown below */}
            {false && (
              <div className="flex justify-center mb-6 sm:mb-8">
                <div className="inline-flex rounded-xl border border-zinc-200 p-1 bg-zinc-50 shadow-sm" onWheel={e => e.preventDefault()}>
                  <button
                    onClick={() => setAuthMethod('email')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      authMethod === 'email'
                        ? 'bg-white text-black shadow-sm'
                        : 'text-zinc-500 hover:text-zinc-700'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <Shield className="h-4 w-4" />
                      <span>Google</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setAuthMethod('whatsapp')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      authMethod === 'whatsapp'
                        ? 'bg-white text-black shadow-sm'
                        : 'text-zinc-500 hover:text-zinc-700'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <Smartphone className="h-4 w-4" />
                      <span>WhatsApp</span>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* WhatsApp OTP — primary method, shown first (Google is below) */}
            {WHATSAPP_ENABLED ? (
              <form onSubmit={handleWhatsAppAuth} className="space-y-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-gradient-to-br from-zinc-50 via-white to-zinc-100 px-4 text-sm text-gray-500">
                      <MessageCircle className="h-5 w-5 inline-block mr-2 text-green-500" />
                      {currentMode === 'register' ? 'Create Account' : 'Sign In'} via WhatsApp
                    </span>
                  </div>
                </div>

                {/* Name is NOT asked at login — it's collected later on the
                    mutual-transfer form and set on the account then. Email stays
                    optional (only for register-mode callers). */}
                {currentMode === 'register' && (
                  <>
                    <div className="space-y-3">
                      <label htmlFor="whatsapp-email" className="block text-sm font-medium text-gray-700">
                        Email Address <span className="text-gray-400">(Optional)</span>
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          id="whatsapp-email"
                          value={email}
                          onChange={handleEmailChange}
                          onKeyDown={handleKeyDown}
                          placeholder="Enter your email address"
                          className="block w-full h-12 rounded-xl border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-base pl-4 pr-12 text-black placeholder-gray-400"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                      </div>
                      {emailError && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <svg className="h-4 w-4 text-red-500 mr-1" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          {emailError}
                        </p>
                      )}
                    </div>
                  </>
                )}

                <div className="space-y-3">
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <div className="relative flex items-center">
                    <PhoneInput
                      country={'in'}
                      value={phoneNumber}
                      onChange={(value: string) => setPhoneNumber(value)}
                      inputStyle={{
                        height: '48px',
                        border: 'none',
                        borderRadius: '0 0.75rem 0.75rem 0',
                        width: '100%',
                        fontSize: '1rem',
                        paddingLeft: '52px',
                        background: 'transparent',
                        color: '#18181b',
                      }}
                      buttonStyle={{
                        border: 'none',
                        borderRight: '1px solid #e4e4e7',
                        borderRadius: '0.75rem 0 0 0.75rem',
                        background: 'transparent',
                        width: '46px',
                        color: '#18181b',
                      }}
                      containerStyle={{
                        width: '100%',
                        display: 'flex',
                        background: 'white',
                        color: '#18181b',
                        border: '1px solid #e4e4e7',
                        borderRadius: '0.75rem',
                      }}
                      dropdownStyle={{
                        borderRadius: '0.75rem',
                        zIndex: 10000,
                        background: 'white',
                        color: '#18181b',
                      }}
                      specialLabel=""
                      inputProps={{
                        id: 'phoneNumber',
                        required: true,
                        // No autoFocus: on mobile it pops the keyboard + autofill on open
                        // and scrolls the field to the top, breaking the centered modal.
                        autoFocus: false,
                        onKeyDown: handleKeyDown,
                        className: 'phone-input-custom',
                      }}
                    />
                    <div className="absolute right-3">
                      <Smartphone className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                  
                  {otpSentSuccess && (
                    <div className="mt-3 rounded-xl bg-zinc-50 p-3 border border-zinc-100">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <CheckCircle2 className="h-5 w-5 text-zinc-400" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-green-700">
                            Verification code sent successfully to your WhatsApp!
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {otpSent && (
                  <div className="space-y-3">
                    <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                      Verification Code
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="otp"
                        value={otp}
                        onChange={(e) => {
                          setOtp(e.target.value);
                          if (otpSentSuccess) {
                            setOtpSentSuccess(false);
                          }
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder="Enter 6-digit code"
                        className="block w-full h-12 rounded-xl border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-base pl-4 pr-12 text-black placeholder-gray-400"
                        required
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <MessageCircle className="h-5 w-5 text-green-500" />
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-gray-500 flex items-center">
                      <CheckCircle2 className="h-4 w-4 text-green-500 mr-1" />
                      Enter the 6-digit code sent to your WhatsApp
                    </p>
                    
                    {/* Resend OTP Section */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-600">
                          Didn't receive the code?
                        </p>
                        {canResend ? (
                          <button
                            type="button"
                            onClick={handleResendOTP}
                            disabled={isLoading}
                            className="text-sm font-medium text-green-600 hover:text-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {isLoading ? 'Sending...' : 'Resend Code'}
                          </button>
                        ) : (
                          <p className="text-sm text-gray-500">
                            Resend in {resendTimer}s
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="text-red-500 text-sm bg-red-50/80 p-3 sm:p-4 rounded-xl border border-red-200/50 backdrop-blur-sm">
                    <div className="flex items-start">
                      <svg className="h-5 w-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-red-700">{error}</p>
                        {error.includes('Google') && (
                          <button
                            type="button"
                            className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                            onClick={async () => {
                              setIsLoading(true);
                              try {
                                const response = await fetch('/api/auth/set-password', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ email }),
                                });
                                const data = await response.json();
                                if (response.ok) {
                                  setSuccess('Password setup link sent to your email.');
                                  setError('');
                                } else {
                                  setError(data.error || 'Failed to send password setup email.');
                                }
                              } catch {
                                setError('Failed to send password setup email.');
                              } finally {
                                setIsLoading(false);
                              }
                            }}
                            disabled={isLoading}
                          >
                            {isLoading ? 'Sending...' : 'Set Password'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-base font-medium text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isLoading ? (
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : null}
                  {isLoading ? 'Processing...' : otpSent ? 'Verify Code' : (currentMode === 'register' ? 'Create Account' : 'Sign In')}
                </button>
              </form>
            ) : (
              <div className="mt-6">
                {!EMAIL_PASSWORD_ENABLED ? null : isForgotPassword ? (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                        Email address
                      </label>
                      <div className="mt-1">
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={email}
                          onChange={handleEmailChange}
                          className="appearance-none block w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-zinc-500/20 focus:border-zinc-500 text-sm transition-all duration-200 bg-white/50 backdrop-blur-sm text-gray-900"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full flex justify-center py-2.5 sm:py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-zinc-600 hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zinc-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : isForgotPassword ? (
                        'Send Reset Link'
                      ) : (
                        'Send Reset Link'
                      )}
                    </button>
                  </form>
                ) : registrationStep === 'otp' ? (
                  <form onSubmit={handleVerifyOtp} className="space-y-6">
                    <div className="flex flex-col items-center">
                      <label htmlFor="otp" className="block text-base font-semibold text-zinc-800 mb-2">
                        Enter the 6-digit OTP sent to your email
                      </label>
                      <input
                        id="otp"
                        ref={otpInputRef}
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                        className="w-40 px-4 py-3 border-2 border-emerald-400 rounded-xl text-center text-lg tracking-widest focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 bg-white text-zinc-900 shadow-sm mb-2"
                        placeholder="123456"
                        maxLength={6}
                        pattern="[0-9]{6}"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        required
                      />
                      <button type="submit" disabled={isLoading || otp.length !== 6} className="w-40 mt-4 bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:bg-emerald-300 disabled:cursor-not-allowed">
                        {isLoading ? 'Verifying...' : 'Verify'}
                      </button>
                    </div>
                    {error && (
                      <div className="mt-4 p-3 bg-red-100 border border-red-300 text-red-800 rounded-lg text-sm text-center">
                        {error}
                      </div>
                    )}
                    {success && (
                      <div className="mt-4 p-3 bg-green-100 border border-green-300 text-green-800 rounded-lg text-sm text-center">
                        {success}
                      </div>
                    )}
                  </form>
                ) : authMethod === 'email' ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {currentMode === 'register' && !isForgotPassword && (
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                          Full name
                        </label>
                        <div className="mt-1">
                          <input
                            id="name"
                            name="name"
                            type="text"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="appearance-none block w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-zinc-500/20 focus:border-zinc-500 text-sm transition-all duration-200 bg-white/50 backdrop-blur-sm text-gray-900"
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                        Email address
                      </label>
                      <div className="mt-1">
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={email}
                          onChange={handleEmailChange}
                          className="appearance-none block w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-zinc-500/20 focus:border-zinc-500 text-sm transition-all duration-200 bg-white/50 backdrop-blur-sm text-gray-900"
                        />
                      </div>
                    </div>

                    {!isForgotPassword && (
                      <div>
                        <div className="flex items-center justify-between mb-1 sm:mb-1.5">
                          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                            Password
                          </label>
                          {currentMode === 'login' && (
                            <button
                              type="button"
                              onClick={() => setIsForgotPassword(true)}
                              className="text-sm font-medium text-zinc-600 hover:text-zinc-500"
                            >
                              Forgot password?
                            </button>
                          )}
                        </div>
                        <div className="mt-1 relative">
                          <input
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="appearance-none block w-full px-3 sm:px-4 py-2 sm:py-2.5 pr-10 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-zinc-500/20 focus:border-zinc-500 text-sm transition-all duration-200 bg-white/50 backdrop-blur-sm text-gray-900"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {showPassword ? (
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                              </svg>
                            ) : (
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {error && (
                      <div className="text-red-500 text-sm bg-red-50/80 p-3 sm:p-4 rounded-xl border border-red-200/50 backdrop-blur-sm">
                        <div className="flex items-start">
                          <svg className="h-5 w-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <div className="flex-1">
                            <p className="text-red-700">{error}</p>
                            {error.includes('Google') && (
                              <button
                                type="button"
                                className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                                onClick={async () => {
                                  setIsLoading(true);
                                  try {
                                    const response = await fetch('/api/auth/set-password', {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ email }),
                                    });
                                    const data = await response.json();
                                    if (response.ok) {
                                      setSuccess('Password setup link sent to your email.');
                                      setError('');
                                    } else {
                                      setError(data.error || 'Failed to send password setup email.');
                                    }
                                  } catch {
                                    setError('Failed to send password setup email.');
                                  } finally {
                                    setIsLoading(false);
                                  }
                                }}
                                disabled={isLoading}
                              >
                                {isLoading ? 'Sending...' : 'Set Password'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {success && (
                      <div className="text-green-600 text-sm bg-green-50/80 p-3 sm:p-4 rounded-xl border border-green-200/50 backdrop-blur-sm">
                        <div className="flex items-start">
                          <svg className="h-5 w-5 text-green-400 mt-0.5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" clipRule="evenodd" />
                          </svg>
                          <div className="flex-1">
                            <p className="text-green-700">{success}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full flex justify-center py-2.5 sm:py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-zinc-600 hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zinc-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? (
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : isForgotPassword ? (
                          'Send Reset Link'
                        ) : (
                          actionButtonText || (currentMode === 'login' ? 'Sign in' : 'Create account')
                        )}
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {currentMode === 'register' && !isForgotPassword && (
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                          Full name
                        </label>
                        <div className="mt-1">
                          <input
                            id="name"
                            name="name"
                            type="text"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="appearance-none block w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-zinc-500/20 focus:border-zinc-500 text-sm transition-all duration-200 bg-white/50 backdrop-blur-sm text-gray-900"
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                        Email address
                      </label>
                      <div className="mt-1">
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={email}
                          onChange={handleEmailChange}
                          className="appearance-none block w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-zinc-500/20 focus:border-zinc-500 text-sm transition-all duration-200 bg-white/50 backdrop-blur-sm text-gray-900"
                        />
                      </div>
                    </div>

                    {!isForgotPassword && (
                      <div>
                        <div className="flex items-center justify-between mb-1 sm:mb-1.5">
                          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                            Password
                          </label>
                          {currentMode === 'login' && (
                            <button
                              type="button"
                              onClick={() => setIsForgotPassword(true)}
                              className="text-sm font-medium text-zinc-600 hover:text-zinc-500"
                            >
                              Forgot password?
                            </button>
                          )}
                        </div>
                        <div className="mt-1 relative">
                          <input
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="appearance-none block w-full px-3 sm:px-4 py-2 sm:py-2.5 pr-10 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-zinc-500/20 focus:border-zinc-500 text-sm transition-all duration-200 bg-white/50 backdrop-blur-sm text-gray-900"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {showPassword ? (
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                              </svg>
                            ) : (
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {error && (
                      <div className="text-red-500 text-sm bg-red-50/80 p-3 sm:p-4 rounded-xl border border-red-200/50 backdrop-blur-sm">
                        <div className="flex items-start">
                          <svg className="h-5 w-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <div className="flex-1">
                            <p className="text-red-700">{error}</p>
                            {error.includes('Google') && (
                              <button
                                type="button"
                                className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                                onClick={async () => {
                                  setIsLoading(true);
                                  try {
                                    const response = await fetch('/api/auth/set-password', {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ email }),
                                    });
                                    const data = await response.json();
                                    if (response.ok) {
                                      setSuccess('Password setup link sent to your email.');
                                      setError('');
                                    } else {
                                      setError(data.error || 'Failed to send password setup email.');
                                    }
                                  } catch {
                                    setError('Failed to send password setup email.');
                                  } finally {
                                    setIsLoading(false);
                                  }
                                }}
                                disabled={isLoading}
                              >
                                {isLoading ? 'Sending...' : 'Set Password'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {success && (
                      <div className="text-green-600 text-sm bg-green-50/80 p-3 sm:p-4 rounded-xl border border-green-200/50 backdrop-blur-sm">
                        <div className="flex items-start">
                          <svg className="h-5 w-5 text-green-400 mt-0.5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" clipRule="evenodd" />
                          </svg>
                          <div className="flex-1">
                            <p className="text-green-700">{success}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full flex justify-center py-2.5 sm:py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-zinc-600 hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zinc-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? (
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : isForgotPassword ? (
                          'Send Reset Link'
                        ) : (
                          actionButtonText || (currentMode === 'login' ? 'Sign in' : 'Create account')
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* OR divider — only when another method is shown above Google */}
            {(WHATSAPP_ENABLED || EMAIL_PASSWORD_ENABLED) && (
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-zinc-200" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-sm font-medium text-zinc-400">OR</span>
                </div>
              </div>
            )}

            {/* Google Sign In — one-click (below WhatsApp) */}
            <div className="w-full bg-zinc-50 border border-zinc-200 rounded-xl shadow-sm">
              <GoogleAuthButton
                mode={currentMode}
                onSuccessCallback={async () => {
                  onClose();
                  await redirectAfterLogin();
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>,
    document.body,
  );
}